import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { NotificationPreferencesService, PreferenceEventState } from '../components/notification-settings/notification-preferences.service';
import { ToastService } from '../../../core/services/toast.service';

interface NotificationPreferencesState {
  preferences: PreferenceEventState[];
  loading: boolean;
}

export const NotificationPreferencesStore = signalStore(
  { providedIn: 'root' },
  withState<NotificationPreferencesState>({
    preferences: [],
    loading: false,
  }),
  withComputed((store) => ({
    allEnabled: computed(() => {
      const prefs = store.preferences();
      if (!prefs.length) return false;
      return prefs.every(e => e.channels.filter(c => c.configurable).every(c => c.enabled));
    }),
  })),
  withMethods((store, svc = inject(NotificationPreferencesService), toast = inject(ToastService)) => {
    // Bumped by every mutating call below. A bulk response only gets applied if
    // no newer edit has started since — otherwise it would clobber a fresher change.
    let requestToken = 0;

    return {
      async load() {
        patchState(store, { loading: true });
        try {
          const { events } = await firstValueFrom(svc.getPreferences());
          patchState(store, { preferences: events, loading: false });
        } catch {
          patchState(store, { loading: false });
          toast.showError('Failed to load notification preferences');
        }
      },

      async toggleChannel(eventType: string, channel: string, enabled: boolean) {
        const token = ++requestToken;
        patchState(store, {
          preferences: store.preferences().map(p =>
            p.event_type === eventType
              ? { ...p, channels: p.channels.map(c => c.channel === channel ? { ...c, enabled } : c) }
              : p
          ),
        });
        try {
          await firstValueFrom(svc.updatePreference(eventType, channel, enabled));
        } catch {
          if (token !== requestToken) return; // a newer edit superseded this; don't clobber it
          patchState(store, {
            preferences: store.preferences().map(p =>
              p.event_type === eventType
                ? { ...p, channels: p.channels.map(c => c.channel === channel ? { ...c, enabled: !enabled } : c) }
                : p
            ),
          });
          toast.showError('Failed to update preference');
        }
      },

      async toggleColumn(channel: string, enabled: boolean) {
        const token = ++requestToken;
        const previous = store.preferences();
        patchState(store, {
          preferences: previous.map(p => ({
            ...p,
            channels: p.channels.map(c => c.channel === channel && c.configurable ? { ...c, enabled } : c),
          })),
        });
        try {
          const { events } = await firstValueFrom(
            enabled ? svc.selectAllForChannel(channel) : svc.deselectAllForChannel(channel)
          );
          if (token !== requestToken) return; // a newer edit superseded this; don't clobber it
          patchState(store, { preferences: events });
        } catch {
          toast.showError('Failed to update preferences');
          try {
            const { events } = await firstValueFrom(svc.getPreferences());
            if (token !== requestToken) return;
            patchState(store, { preferences: events });
          } catch {
            if (token !== requestToken) return;
            patchState(store, { preferences: previous }); // recovery also failed — restore prior known state
          }
        }
      },

      async selectAll(enabled: boolean) {
        const token = ++requestToken;
        const previous = store.preferences();
        patchState(store, {
          preferences: previous.map(p => ({
            ...p,
            channels: p.channels.map(c => c.configurable ? { ...c, enabled } : c),
          })),
        });
        try {
          const { events } = await firstValueFrom(enabled ? svc.selectAll() : svc.deselectAll());
          if (token !== requestToken) return;
          patchState(store, { preferences: events });
        } catch {
          toast.showError('Failed to update preferences');
          try {
            const { events } = await firstValueFrom(svc.getPreferences());
            if (token !== requestToken) return;
            patchState(store, { preferences: events });
          } catch {
            if (token !== requestToken) return;
            patchState(store, { preferences: previous }); // recovery also failed — restore prior known state
          }
        }
      },
    };
  })
);
